import {
  criteriaMatch,
  matchesCriterion,
  normalizeCriteria,
  type Criterion,
  type CriteriaOp,
  type FilterableNode,
} from './search-criteria';

// The criterion grammar started here and moved to `search-criteria.ts` when it
// became the app-wide search grammar (palette, /search, Work list). Re-exported
// so map-side importers keep one import path for "the map view model".
export { matchesCriterion, normalizeCriteria };
export type { Criterion, CriteriaOp, FilterableNode };

/**
 * A **map view**: the serializable bundle of options a viewer applies to the work
 * network (filters, lenses, the per-view hidden set, and later the encoding
 * bindings). It is pure data, no rendering and no I/O, so it is the one shape the
 * canvas holds in transient state today and the one the saved-view primitive
 * persists later (P4): "save this view" is just storing this object. Keeping it in
 * `domain/` means both the renderer and the (future) server validate against the
 * same definition.
 *
 * Note what this is NOT: a stored node position. Layout is ELK's job and re-flows as
 * the graph changes (see docs/MAP_VIEW.md §4), so a view carries *what to show and
 * how to weight it*, never *where a node sits*.
 */
export interface MapView {
  /** Hard dependency edges ("A is blocked by B"). */
  showBlocks: boolean;
  /** Soft dependency edges ("relates to"). */
  showRelates: boolean;
  /** Draw arrowheads on dependency edges. */
  showArrows: boolean;
  /** Keep only unclaimed work (the "needs doing" first-class state). */
  needsDoingOnly: boolean;
  /** Spotlight flagged items, pushing the rest back as context. */
  focusFlagged: boolean;
  /** Typed filter criteria (the filter card), combined with `criteriaOp`. */
  criteria: Criterion[];
  /** How the criteria combine: every one must match, or any one suffices. */
  criteriaOp: CriteriaOp;
  /** The group (its id) the lens is trained on, or null for the whole graph. */
  lens: string | null;
  /** `fade`: dim non-members as context; `only`: drop them from the view. */
  lensMode: LensMode;
  /** Tree scopes narrowing the view to chosen trees/components; empty = the whole
   *  visible graph. Several scopes are **OR-ed** (their node sets union), so two
   *  subtree scopes show both trees side by side. Replaces the old single
   *  `subtreeRoot` (normalizeView still reads legacy payloads). */
  scopes: Scope[];
  /** Per-view hidden node ids (an exclude set, not a position). */
  hidden: string[];
}

export type LensMode = 'fade' | 'only';

/**
 * How one scope gathers its nodes:
 * - `subtree`: the root plus its decomposition descendants, with the root's
 *   ancestor chain kept as **collapsed context** (visible-but-quiet, never dropped).
 * - `connected`: the whole connected component around the node, flood-filled over
 *   BOTH edge kinds (decomposition + dependency), direction ignored.
 */
export type ScopeMode = 'subtree' | 'connected';
export const SCOPE_MODES = ['subtree', 'connected'] as const;

/** One tree/component scope (see `MapView.scopes`; resolution is topological and
 *  lives with the renderer in `view-filter.ts`, like the subtree lens before it). */
export interface Scope {
  mode: ScopeMode;
  rootId: string;
}

const scopeKey = (s: Scope) => `${s.mode}:${s.rootId}`;

export const DEFAULT_VIEW: MapView = {
  showBlocks: true,
  showRelates: true,
  showArrows: true,
  needsDoingOnly: false,
  focusFlagged: false,
  criteria: [],
  criteriaOp: 'all',
  lens: null,
  lensMode: 'fade',
  scopes: [],
  hidden: [],
};

/**
 * The pure per-node filter predicate: does this node survive the view's node
 * filters? The quick toggle (needs doing) AND the typed criteria (combined by
 * `criteriaOp`, via the shared `criteriaMatch`) must all pass. Set membership
 * (lens/subtree/hidden) is topological and lives with the renderer; this is the
 * field-level part. Hiding CLOSED work is no longer a map-fragment toggle: the
 * lens's `showClosed` flag governs every rail, applied where the lens's matches
 * are gathered (docs/VIEWS.md "Closed items"), and the map rail is narrowed to
 * that match set - so a closed item never reaches this predicate.
 */
export function nodeMatchesFilters(node: FilterableNode, view: MapView): boolean {
  if (view.needsDoingOnly && node.contributors.length > 0) return false;
  return criteriaMatch(node, view.criteria, view.criteriaOp);
}

/**
 * Whether two views are equivalent (the hidden set compared order-independently).
 * Used to tell a loaded saved view from one the viewer has since tweaked ("unsaved
 * changes"), so the UI offers an update only when something actually changed.
 */
export function sameView(a: MapView, b: MapView): boolean {
  return (
    a.showBlocks === b.showBlocks &&
    a.showRelates === b.showRelates &&
    a.showArrows === b.showArrows &&
    a.needsDoingOnly === b.needsDoingOnly &&
    a.focusFlagged === b.focusFlagged &&
    a.criteriaOp === b.criteriaOp &&
    JSON.stringify(a.criteria) === JSON.stringify(b.criteria) &&
    a.lens === b.lens &&
    a.lensMode === b.lensMode &&
    // Scopes union (OR), so order is presentation only: compare as a set.
    a.scopes.length === b.scopes.length &&
    [...a.scopes].map(scopeKey).sort().join(' ') === [...b.scopes].map(scopeKey).sort().join(' ') &&
    a.hidden.length === b.hidden.length &&
    [...a.hidden].sort().join(' ') === [...b.hidden].sort().join(' ')
  );
}

/**
 * Coerce arbitrary (stored or wire) input into a valid `MapView`, filling missing
 * fields from the default and dropping anything unknown or wrongly typed. The saved
 * view primitive stores this object as JSON, so the server validates the payload with
 * this on the way in and the read normalizes it on the way out: a payload from an
 * older or hand-edited row can never put a bad value on the canvas.
 */
export function normalizeView(input: unknown): MapView {
  const o = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  return {
    showBlocks: bool(o.showBlocks, DEFAULT_VIEW.showBlocks),
    showRelates: bool(o.showRelates, DEFAULT_VIEW.showRelates),
    showArrows: bool(o.showArrows, DEFAULT_VIEW.showArrows),
    needsDoingOnly: bool(o.needsDoingOnly, DEFAULT_VIEW.needsDoingOnly),
    focusFlagged: bool(o.focusFlagged, DEFAULT_VIEW.focusFlagged),
    criteria: normalizeCriteria(o.criteria),
    criteriaOp: o.criteriaOp === 'any' ? 'any' : 'all',
    lens: str(o.lens),
    lensMode: o.lensMode === 'only' ? 'only' : 'fade',
    scopes: normalizeScopes(o.scopes, o.subtreeRoot),
    hidden: Array.isArray(o.hidden)
      ? o.hidden.filter((x): x is string => typeof x === 'string')
      : [],
  };
}

/** Validate stored scopes: keep well-formed `{mode, rootId}` entries (deduped; ids
 *  are only type-checked - existence is the graph's concern). A legacy payload's
 *  `subtreeRoot: x` (the pre-scopes single-root filter) folds into one subtree
 *  scope, so older saved views round-trip without a migration. */
function normalizeScopes(input: unknown, legacySubtreeRoot: unknown): Scope[] {
  const out: Scope[] = [];
  const seen = new Set<string>();
  const push = (s: Scope) => {
    const key = scopeKey(s);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(s);
  };
  if (Array.isArray(input)) {
    for (const raw of input) {
      if (!raw || typeof raw !== 'object') continue;
      const s = raw as Record<string, unknown>;
      if (
        typeof s.rootId === 'string' &&
        s.rootId.length > 0 &&
        (SCOPE_MODES as readonly string[]).includes(s.mode as string)
      ) {
        push({ mode: s.mode as ScopeMode, rootId: s.rootId });
      }
    }
  }
  if (out.length === 0 && typeof legacySubtreeRoot === 'string' && legacySubtreeRoot.length > 0) {
    push({ mode: 'subtree', rootId: legacySubtreeRoot });
  }
  return out;
}
